/**
 * Willow — „Buffy the Vampire Slayer".
 *
 * Keď Willow alebo Tara utŕži poškodenie, Willow sa stane Dark Willow (verejné,
 * `players[i].form`: `dark`). Na konci ťahu, ak Dark Willow susedí s Tarou,
 * vráti sa. Efekty kariet označené (DARK) platia len pre Dark Willow.
 */

import { areAdjacent, shareZone } from '../map.js';
import type { Ctx } from '../ops.js';
import { askChoice, drawCard, emit, getMap, getPlayer, millDeck, setHealth } from '../ops.js';
import type { PlayerIndex } from '../types.js';
import { blindBoost } from './doctorStrange.js';
import { feint, regroup } from './common.js';
import { askMove, emptySpacesInZone, fighterOrNull } from './helpers.js';
import { swiftStrike } from './kingArthur.js';
import type { CardEffect, EffectCtx, EffectRegistry, HeroSpecial } from './types.js';

const WILLOW = 'willow';
const TARA = 'tara';
const HACKER = 'willow.hacker';
const RESURRECT = 'willow.resurrect';

const figure = (ctx: Ctx, player: PlayerIndex, defId: string) =>
  ctx.state.fighters.find((f) => f.owner === player && f.defId === defId) ?? null;

export const isDark = (ctx: Ctx, player: PlayerIndex) => ctx.state.players[player]?.form === 'dark';

function setDark(ctx: Ctx, player: PlayerIndex, dark: boolean): void {
  const seat = ctx.state.players[player];
  if (!seat || isDark(ctx, player) === dark) return;
  seat.form = dark ? 'dark' : 'light';
  emit(ctx, { type: 'specialUsed', player, heroId: WILLOW, name: dark ? 'Willow je Dark Willow' : 'Willow sa vracia' });
}

const side = (e: EffectCtx) => (e.role === 'defender' ? 'defender' : 'attacker');

/** „Move the opposing fighter up to 3 spaces." */
const rendingShot: CardEffect = {
  after: (e) => {
    if (e.opponentFighterId) askMove(e, { fighters: [e.opponentFighterId], max: 3, label: 'Rending Shot: presuň súpera až o 3' });
  },
};

/** „(DARK) Become Willow." */
const meditation: CardEffect = {
  after: (e) => {
    if (isDark(e.ctx, e.player)) setDark(e.ctx, e.player, false);
  },
};

/** „(DARK) BLIND BOOST this card." — povinné, nie „may". */
const darkBlindBoost: CardEffect = {
  duringChoice: (e) => {
    if (isDark(e.ctx, e.player)) blindBoost(e.ctx, e.player, side(e));
  },
};

/**
 * „Draw 2 cards. (DARK) Discard the top 2 cards of your deck. Deal 3 damage to
 * a sidekick in your zone (even if it's your own)."
 */
const loveAndLoss: CardEffect = {
  scheme: (e) => {
    drawCard(e.ctx, e.player);
    drawCard(e.ctx, e.player);
    if (!isDark(e.ctx, e.player)) return;
    millDeck(e.ctx, e.player, 2);
    const willow = figure(e.ctx, e.player, WILLOW);
    if (!willow) return;
    const map = getMap(e.ctx);
    const targets = e.ctx.state.fighters
      .filter((f) => f.kind === 'sidekick' && shareZone(map, f.spaceId, willow.spaceId))
      .map((f) => f.id);
    if (targets.length === 0) return;
    askChoice(e.ctx, {
      kind: 'damage',
      player: e.player,
      source: e.cardId,
      label: 'Love and Loss: 3 poškodenia sidekickovi vo svojej zóne (aj vlastnému)',
      optional: false,
      amount: 3,
      targets,
    });
  },
};

/**
 * „Look at the top card of your deck, then choose to put it on the top of your
 * deck or the bottom of your deck." Kartu vidí len majiteľ (voľba z balíčka);
 * výber = pod balíček, preskočenie = ostane navrchu.
 */
const hacker: CardEffect = {
  after: (e) => {
    const top = getPlayer(e.ctx.state, e.player).deck[0];
    if (!top) return;
    askChoice(e.ctx, {
      kind: 'card',
      player: e.player,
      source: e.cardId,
      label: 'Hacker: vrchná karta balíčka — klikni na ňu, ak má ísť pod balíček (inak ostane navrchu)',
      optional: true,
      from: 'deck',
      cards: [top],
      purpose: 'effect',
    });
  },
  onCardChosen: (ctx, choice, cardId) => {
    const deck = getPlayer(ctx.state, choice.player).deck;
    if (deck[0] !== cardId) return;
    deck.push(deck.shift()!);
    emit(ctx, { type: 'cardsToBottom', player: choice.player, count: 1 });
  },
};

/** „(DARK) Move Willow to any space in her zone. Discard the top card of your deck." */
const whenGoodMagicFails: CardEffect = {
  after: (e) => {
    if (!isDark(e.ctx, e.player)) return;
    const willow = figure(e.ctx, e.player, WILLOW);
    const spaces = willow ? emptySpacesInZone(e.ctx, willow.id) : [];
    if (willow && spaces.length > 0) {
      askChoice(e.ctx, {
        kind: 'place',
        player: e.player,
        source: e.cardId,
        label: 'When Good Magic Fails: polož Willow v jej zóne',
        optional: false,
        fighterId: willow.id,
        spaces,
      });
    }
    millDeck(e.ctx, e.player, 1);
  },
};

/** „If you won the combat, choose a card in your discard pile and add it to your hand." */
const knowledgeOfTheCraft: CardEffect = {
  after: (e) => {
    const discard = getPlayer(e.ctx.state, e.player).discard;
    if (!e.won || discard.length === 0) return;
    askChoice(e.ctx, {
      kind: 'card',
      player: e.player,
      source: e.cardId,
      label: 'Knowledge of the Craft: vezmi si kartu z odhodu do ruky',
      optional: false,
      from: 'discard',
      cards: discard.slice(),
      purpose: 'toHand',
    });
  },
};

/**
 * „Choose a friendly fighter who has been defeated. Place that fighter in any
 * space in Willow's zone and set their health to 3."
 */
const resurrect: CardEffect = {
  scheme: (e) => {
    const willow = figure(e.ctx, e.player, WILLOW);
    const fallen = (e.ctx.state.defeated ?? []).filter((f) => f.owner === e.player);
    if (!willow || fallen.length === 0) return;
    askChoice(e.ctx, {
      kind: 'option',
      player: e.player,
      source: e.cardId,
      label: 'Resurrect: koho privoláš späť?',
      optional: false,
      options: fallen.map((f) => ({ label: f.name, follow: { kind: 'effect' as const, effect: RESURRECT, data: `pick:${f.id}` } })),
    });
  },
  onFollow: (ctx, choice, data) => {
    const [step, id] = data.split(':') as [string, string];
    if (step === 'hp') {
      if (fighterOrNull(ctx, id)) setHealth(ctx, id, 3);
      return;
    }
    const willow = figure(ctx, choice.player, WILLOW);
    const spaces = willow ? emptySpacesInZone(ctx, willow.id) : [];
    if (spaces.length === 0) return;
    askChoice(ctx, {
      kind: 'place',
      player: choice.player,
      source: choice.source,
      label: 'Resurrect: polož figúrku v zóne Willow',
      optional: false,
      fighterId: id,
      spaces,
    });
    // `placeFighter` vráti porazenú figúrku s plným zdravím — karta hovorí 3.
    askChoice(ctx, {
      kind: 'auto',
      player: choice.player,
      source: choice.source,
      label: 'Resurrect: zdravie 3',
      optional: false,
      follow: { kind: 'effect', effect: RESURRECT, data: `hp:${id}` },
    });
  },
};

export const willowCards: EffectRegistry = {
  'willow.rending-shot': rendingShot,
  'willow.meditation': meditation,
  'willow.black-magic': darkBlindBoost,
  'willow.love-and-loss': loveAndLoss,
  'willow.flayed-alive': darkBlindBoost,
  [HACKER]: hacker,
  'willow.when-good-magic-fails': whenGoodMagicFails,
  'willow.knowledge-of-the-craft': knowledgeOfTheCraft,
  [RESURRECT]: resurrect,
  // „Cancel any abilities on your opponent's card." = zrušenie efektov ako Feint.
  'willow.revoke': feint,
  'willow.swift-strike': swiftStrike,
  'willow.feint': feint,
  'willow.regroup': regroup,
};

export const willowSpecial: HeroSpecial = {
  name: 'Dark Willow',
  /** „When Willow or Tara is dealt damage, Willow becomes Dark Willow." */
  onDamaged: (ctx, player, fighter) => {
    if (fighter.defId === WILLOW || fighter.defId === TARA) setDark(ctx, player, true);
  },
  /** „At the end of your turn, if Dark Willow is adjacent to Tara, she becomes Willow." */
  endOfTurn: (ctx, player) => {
    const willow = figure(ctx, player, WILLOW);
    const tara = figure(ctx, player, TARA);
    if (isDark(ctx, player) && willow && tara && areAdjacent(getMap(ctx), willow.spaceId, tara.spaceId)) {
      setDark(ctx, player, false);
    }
  },
};

